<template>
	<div id="qpe_line_chart" ref="line_chart">
        <div :id="id" :style="{'text-align': 'center'}">
        </div>
    </div>
</template>

<script>
    import { 
        get_diagram_shape,
        init_chart_resize_observer,
    } from '@/utils/chart/common.js'
    import { 
        get_container_instance,
        draw_background_for_screenshot,
    } from '@/utils/chart/background.js'
    import {
        draw_x_axis_label,
        get_scale_time,
        draw_x_axis_time_tick,
        draw_day_line,
    } from '@/utils/chart/x_axis.js'
    import {
        draw_y_axis_label,
        get_scale_y,
        draw_y_axis_tick,
        draw_y_axis_basic_background,
    } from '@/utils/chart/y_axis.js'
    import { 
        draw_cross,
        draw_triangle,
        draw_triangle_text,
    } from '@/utils/chart/graph.js'
    import { 
        draw_tooltip,
        get_content_instance,
    } from '@/utils/chart/tooltip.js'
    import { judge_value_valid } from '@/utils/chart/verification.js'
    import { transform_datetime_str_to_chart_need_format } from '@/utils/common/custom_time.js'
    import { take_screenshot } from '@/utils/common/screenshot.js'
    import { mapState } from 'vuex'
    import * as d3 from 'd3';

	export default {
        name: 'qpe_line_chart',
        
        props: {
            // 同一頁面超過一個 d3 圖時就需要用到
            id: {
                type: String,
                default() {
                    return 'unique_chart'
                }
            },
            // 原始資料
            choosed_time_series_data: {
                type: Array,
                default () {
                    return []
                }
            },
            // 選擇測站的時間
            x_axis_time_series_data: {
                type: Array,
                default () {
                    return []
                }
            },
            basic_layout_setting: {
                type: Object,
                default () {
                    // margin.left 不能小於 25
                    return {
                        'height': 500,
                        'margin': {
                            'top': 80,
                            'right': 50,
                            'bottom': 45,
                            'left': 30,
                        }
                    }
                }
            },
            // 直方圖主表題
            svg_main_title: {
                type: String,
                default () {
                    return '我是 props 主標題'
                }
            },
            // 直方圖副標題
            svg_sub_title: {
                type: String,
                default () {
                    return '我是 props 副標題'
                }
            },
            // X軸圖說
            // X軸 ticks *不是數字打幾就會出現幾個 tick
            // X軸 display_interval 每幾個 tick 會顯示值
            x_axis: {
                type: Object,
                default () {
                    return {
                        'label': '我是 props X軸圖說',
                        'ticks': 48,
                        'display_interval': 4,
                        'label_distance': 40
                    }
                }
            },
            // y軸圖說
            // y軸 ticks * 不是數字打幾就會出現幾個間隔
            y_axis: {
                type: Object,
                default () {
                    return {
                        'label': '我是 props Y軸圖說',
                        'ticks': 10,
                        'display_interval': 1,
                        'max_value': 100,
                        'min_value': 0,
                        'label_distance': 60
                    }
                }
            },
            show_day_line: {
                type: Boolean,
                default () {
                   return true
                }
            },
            show_tick_color: {
                type: Boolean,
                default () {
                    return true
                }
            },
            show_extreme_value: {
                type: Boolean,
                default () {
                    return false
                }
            },
            show_data_end_time: {
                type: Boolean,
                default () {
                    return true
                }
            },
            show_path: {
                type: Boolean,
                default () {
                    return true
                }
            },
            draw_legend_function: {
                type: Function,
                default () {
                    return function() {}
                }
            },
            draw_custom_function: {
                type: Function,
                default() {
                    return () => {}
                }
            },
            tooltip_value_text: {
                type: String,
                default() {
                    return this.$t('__chart_common.tooltip_value')
                }
            },

            tooltip_value_unit: {
                type: String,
                default() {
                    return ''
                }
            },

            tooltip_value_icon_name: {
                type: String,
                default() {
                    return ''
                }
            },

            gust_wind_data: {
                type: Array,
                default: () => [],
            },

            gust_timeseries_data: {
                type: Array,
                default: () => [],
            },
        }, 

        data() {
            return {
                width: 0,
                height: 0,
                resize_observer: null,
            }
        },
        
		computed: {
            diagram_shape() {
                return get_diagram_shape(this.choosed_time_series_data)
            }
        },

        created() {
            this.$nextTick(() => { 
                this.resize_observer = !this.resize_observer && init_chart_resize_observer.call(this, this.$refs.line_chart)
                this.resize_observer.observe(document.querySelector('.display-block__wrapper'))
            })
        },

        beforeDestroy() {
            this.resize_observer.unobserve(this.$refs.line_chart)
        },

		methods: {
            rerender_svg() {
                d3.selectAll(`#${this.id} > svg`).remove();
                d3.selectAll(`#${this.id} > .tooltip`).remove();
                d3.selectAll(`#${this.id} > .tooltip-line`).remove();
                d3.select(`#${this.id} > .drag_svg`)
                    .on('start', null)
                    .on('drag', null)
                    .on('end', null)
                this.init_pic()
            },
            init_pic() {
                const common_options = {
                    id: this.id, 
                    width: this.width, 
                    height: this.height, 
                    margin: this.basic_layout_setting.margin
                }
                
                // 建立 svg 範圍
                const container = get_container_instance(common_options)
                draw_background_for_screenshot(container, common_options)

                const svg = container
                    .append('g')

                // X軸 圖說 (時間)
                const x_axis_label_options = {
                    ...common_options,
                    ...{ x_axis: this.x_axis }
                }
                draw_x_axis_label(svg, x_axis_label_options)
                
                // Y軸圖說
                const y_axis_label_options = {
                    ...common_options,
                    ...{ y_axis: this.y_axis }
                }
                draw_y_axis_label(svg, y_axis_label_options)

                const scale_time_options = {
                    ...common_options,
                    ...{
                        start_time_str: this.x_axis_time_series_data[0],
                        end_time_str: this.x_axis_time_series_data.slice(-1)[0],
                    }
                }
                const scale_time = get_scale_time(scale_time_options)
                const x_axis_tick_options = {
                    ...common_options,
                    ...{ 
                        x_axis: this.x_axis,
                        scale_time: scale_time,
                    }
                }
                draw_x_axis_time_tick(svg, x_axis_tick_options)

                // Y軸
                const scale_y_options = {
                    ...common_options,
                    ...{ y_axis: this.y_axis }
                }
                const scale_y = get_scale_y(scale_y_options)
                
                const y_axis_tick_options = {
                    ...scale_y_options,
                    ...{
                        scale: scale_y
                    }
                }
                draw_y_axis_tick(svg, y_axis_tick_options)

                this.show_tick_color && draw_y_axis_basic_background(svg, common_options)
                this.show_day_line && draw_day_line(svg, { ...common_options, ...{ scale_time } })

                // 內容
                const { width, height, margin } = common_options
                const scale_data = d3.scaleLinear()
                    .domain([0, this.choosed_time_series_data.length-1])
                    .range([0, width])

                if (this.show_path) {
                    const line = d3.line()
                        .defined(d => judge_value_valid(d))
                        .x((d, i) => margin.left + scale_data(i))
                        .y(d => scale_y(d))
                        
                    svg.append('path')
                        .attr('class', 'line')
                        .attr('d', line(this.choosed_time_series_data)); 
                }

                // enter() 用來建立沒有抓到 dom 的 dom
                svg.selectAll('circle')
                    .data(this.choosed_time_series_data)
                    .enter()
                    .append('circle')
                    .style('stroke', '#5e92d3')
                    .style('opacity', this.show_path ? 0 : 1)
                    .attr('fill', (d) => judge_value_valid(d) ? '#121624' : 'gray')
                    // 從左到右
                    .attr('cx', (d, i) => {
                        return margin.left + scale_data(i)
                    })
                    // 從上到下
                    .attr('cy', (d) => {
                        return judge_value_valid(d) ? scale_y(d) : height
                    })
                    .attr('r', (d) => {
                        return judge_value_valid(d) ? 1 : 2
                    })

                const cross_options = {
                    ...common_options,
                    ...{
                        data: this.choosed_time_series_data,
                        scale_data,
                        scale_y,
                    }
                }
                draw_cross(svg, cross_options)

                // 滑鼠覆蓋時的資料提示
                const tooltip_content = get_content_instance(this.id)

                const extreme_value_options = {
                    ...common_options,
                    ...{
                        scale_data,
                        scale_y,
                        tooltip: tooltip_content
                    }
                }
                this.show_extreme_value && this.draw_extreme_value(svg, extreme_value_options)

                this.draw_legend_function(svg, { width, height })
                this.draw_custom_function(svg, width, scale_time, scale_y, tooltip_content, scale_data)
                
                draw_tooltip({
                    basic: {
                        svg,
                        id: this.id,
                        scale_data, 
                        scale_time,
                        scale_y,
                        width,
                        height: this.height, 
                        margin: this.basic_layout_setting.margin
                    },
                    tooltip: {
                        text: this.tooltip_value_text,
                        unit: this.tooltip_value_unit,
                        gust_text: this.$t('__monitor_weather.gust_value'),
                        no_data_text: this.$t('__chart_common.no_data')
                    },
                    data: {
                        content: this.choosed_time_series_data,
                        x_axis: this.x_axis_time_series_data,
                        gust_wind_data: this.gust_wind_data,
                        gust_time_series_data: this.gust_timeseries_data,
                    }
                })
            },

            draw_extreme_value(svg, extreme_value_options) {
                // 極值
                const float_time_series_data = this.choosed_time_series_data.map(val => parseFloat(val))
                const valid_float_time_series_data = float_time_series_data.filter(val => !isNaN(val))
                const extend_value = d3.extent(valid_float_time_series_data) 

                const get_need_extreme_info = (extreme_value) => {
                    const index = float_time_series_data.lastIndexOf(extreme_value)
                    const datetime = transform_datetime_str_to_chart_need_format(this.x_axis_time_series_data[index])    
                    return [index, datetime]
                }

                const max_value = extend_value[1]
                const [max_index, max_datetime] = get_need_extreme_info(max_value)

                const max_value_options = {
                    common: extreme_value_options,
                    detail: {
                        color: 'red',
                        label_distance: -20,
                        label_value_distance: -35,
                        rotate_deg: 180,
                        index: max_index, 
                        value: max_value, 
                        datetime: max_datetime
                    }
                }
                draw_triangle(svg, max_value_options)
                draw_triangle_text(svg, max_value_options)
                
                const min_value = extend_value[0]
                const [min_index, min_datetime] = get_need_extreme_info(min_value)

                const min_value_options = {
                    common: extreme_value_options,
                    detail: {
                        color: 'orange',
                        label_distance: 20,
                        label_value_distance: 40,
                        rotate_deg: 0,
                        index: min_index, 
                        value: min_value, 
                        datetime: min_datetime
                    }
                }
                draw_triangle(svg, min_value_options)
                draw_triangle_text(svg, min_value_options)
            },

            save_png(file_name) {
                const node = document.querySelector('#base-popup__capture_range')
                take_screenshot(node, file_name)
            },
		},
    }
</script>

<style lang="scss">

#qpe_line_chart {
    position: relative;
    height: 30vh;
    margin: 0 auto;
    padding-left: 0.25rem;
    box-sizing: border-box;

    .tooltip {
        position: absolute;
        padding: 0.125rem 0.25rem;
        background: white;
        font: 12px sans-serif;
        text-align: left;
        border-radius: 2px;
        pointer-events: none;
        box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.4);
        white-space: nowrap;
    }

    text {
        font-size: 15px;
    }
    
    .main_title {
        fill: rgb(97, 130, 181);;
        font-weight: bold;
    }

    .title_font_size {
        font-size: 18px;
    }

    .vertical_text {
        -webkit-writing-mode: vertical-lr;
        writing-mode: vertical-lr;
    }

    .date_line line{
        stroke: #ece8f7;
        stroke-width: 2;
        stroke-dasharray: 7;
    }

    .date_line path{
        stroke-dasharray: 2000;
        stroke-dashoffset: 2000;
    }

    .date_line text {
        opacity: 0.85; fill: rgb(192, 192, 192);
    }

    .line {
        fill: none;
        stroke: steelblue;
        stroke-width: 3;
    }

    .tooltip-line {
        position: absolute;
        border-right: 1px solid black;
    }
}

</style>